import React, { useState, useEffect, useMemo } from 'react';
import useDimensions from 'react-use-dimensions';

import PropTypes from 'prop-types';
import { LinearProgress } from '@material-ui/core';

import E from "leaflet-editable"; // eslint-disable-line

import { MapContainer, TileLayer, FeatureGroup, ZoomControl, WMSTileLayer, useMapEvents } from 'react-leaflet';

import { useLeaflet } from 'hooks';

import { TileLayers, LegendControl, WMSLegend, MapOptions, SearchBarControl } from '.';

import './Leaflet.css';

const MapEvents = ({ zoomstart, zoomend, onEdit, setZooming }) => {
  const map = useMapEvents({// eslint-disable-line
    zoomstart: zoomstart,
    zoomend: zoomend,
    'editable:vertex:new': onEdit,
    'editable:vertex:dragend': onEdit,
    'editable:vertex:deleted': onEdit,
    'editable:drawing:end': (e) => {
      onEdit(e);
      e.layer.disableEdit();
      map.editTools.stopDrawing();
    },
  });

  map.on('zoom', function () {
    setZooming(true);
  });

  return null;
};

const Leaflet = ({ children, features, handleEdit, legendKey, onMapCreate, bounds, flyto, setZooming }) => {
  const { baseLayer, opacity, dataLayerConfig, updatePosition } = useLeaflet();

  const [mapRef, mapSize] = useDimensions();
  const [leafletMap, setMap] = useState();
  const [moveBounds, setMoveBounds] = useState(undefined);
  const [moving, setMoving] = useState(false);

  const tileLayer = TileLayers.layers[baseLayer];

  useEffect(() => {
    if (leafletMap && flyto) {
      leafletMap.flyToBounds(bounds);
    }
  }, [bounds]);

  const moveMap = (pts, zoomLevel) => {
    if (updatePosition) {
      let newmoveBounds;
      let textureBounds;
      if (moveBounds && pts) {
        if (
          pts._northEast.lat > moveBounds[0][0] ||
          pts._northEast.lng > moveBounds[0][1] ||
          pts._southWest.lat < moveBounds[1][0] ||
          pts._southWest.lng < moveBounds[1][1]
        ) {
          const diffLat = Math.abs(pts._northEast.lat - pts._southWest.lat) * 0.4;
          const diffLng = Math.abs(pts._northEast.lng - pts._southWest.lng) * 0.4;
          if (diffLat < 10 && diffLng < 10) {
            newmoveBounds = [
              [pts._northEast.lat + diffLat, pts._northEast.lng + diffLng],
              [pts._southWest.lat - diffLat, pts._southWest.lng - diffLng],
            ];
          }
          textureBounds = [
            [pts._northEast.lat, pts._northEast.lng],
            [pts._southWest.lat, pts._southWest.lng],
          ];
        }
      } else {
        // newmoveBounds = pts; // The first one will be for the area of the screen, thats ok
      }
      updatePosition(pts, textureBounds, zoomLevel);
      if (newmoveBounds) {
        setMoveBounds(newmoveBounds);
      }
    }
  };
  const setIsMoving = () => {
    if (!moving) {
      setMoving(true);
    }
  };

  const setMoved = () => {
    setMoving(false);
    updatePositiondBounced();
  };

  const onEdit = (e, map) => {
    if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
      console.log('Map Event ', e, map);
    }
    const { layer, layerType } = e;

    if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
      console.log('EditPoints updatePolygon...', layerType, layer);
    }
    if (layer._latlngs[0] && layer._latlngs[0].length > 0 && handleEdit) {
      handleEdit(layer.toGeoJSON().geometry, layer);
    }
  };

  const updatePositiondBounced = () => {
    if (leafletMap) {
      const newBounds = leafletMap.getBounds();
      if (!bounds || newBounds._northEast.lat !== bounds[0][0] || newBounds._northEast.lng !== bounds[0][1]) {
        moveMap(newBounds, leafletMap.getZoom());
      }
    }
  };

  const memoChild = useMemo(() => {
    return !moving ? children : null;
  }, [children, moving]);

  const dataLayerMemo = useMemo(() => {
    return dataLayerConfig ? (
      <>
        {dataLayerConfig.type === 'tile' ? (
          <TileLayer key={dataLayerConfig.name} {...dataLayerConfig} zIndex={2} opacity={opacity} />
        ) : (
          <WMSTileLayer
            key={dataLayerConfig.name}
            opacity={opacity}
            layers={dataLayerConfig.layers}
            url={dataLayerConfig.url}
            format="image/png"
            transparent
            zIndex={543}
          />
        )}
        {dataLayerConfig.legend && <WMSLegend key={dataLayerConfig.name} legend={dataLayerConfig.legend} />}
        {dataLayerConfig.grades && !tileLayer.grades && (
          <LegendControl key={dataLayerConfig.name} {...dataLayerConfig} />
        )}
      </>
    ) : null;
  }, [dataLayerConfig, dataLayerConfig?.type, opacity, tileLayer]);

  // If no size has been computed we calculate it here once, the drawback is that if the window is resized, the height
  // will not be recomputed on a resize, but this alleviates some bad stuff from happening.
  if (mapSize?.height === undefined) {
    return (
      <div className="terraleaflet" ref={mapRef}>
        <LinearProgress />
      </div>
    );
  }

  return (
    <div className="terraleaflet" ref={mapRef} id="mapid">
      <MapContainer
        whenReady={(map) => {
          setMap(map?.target);
          if (onMapCreate) {
            onMapCreate(map?.target);
          }
        }}
        bounds={bounds}
        key={baseLayer}
        maxZoom={tileLayer.maxZoom}
        minZoom={1}
        style={{ height: '100vh' }}
        editable={true}
        zoomControl={false}
      >
        <MapEvents zoomstart={setIsMoving} zoomend={setMoved} onEdit={onEdit} setZooming={setZooming} />
        <MapOptions />
        <FeatureGroup>
          <ZoomControl position="topleft" />
        </FeatureGroup>
        <TileLayer {...tileLayer} zIndex={1} />
        {memoChild}
        <FeatureGroup>{features}</FeatureGroup>
        {tileLayer.grades && legendKey !== `${baseLayer}legend` && (
          <LegendControl key={`${baseLayer}legend`} style={{ color: 'red' }} {...tileLayer} />
        )}
        {dataLayerMemo}
        <SearchBarControl position="topright" />
      </MapContainer>
    </div>
  );
};

Leaflet.propTypes = {
  children: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  bounds: PropTypes.array,
  flyto: PropTypes.bool,
  legendKey: PropTypes.string,
  handleEdit: PropTypes.func,
  onMapCreate: PropTypes.func,
  features: PropTypes.array,
  setZooming: PropTypes.func,
};

Leaflet.defaultProps = {
  flyto: true,
  bounds: [
    [40.071237673735865, -75.59282683767378],
    [40.06726382855591, -75.59974157251419],
  ],
  getBounds: undefined,
  dataLayerConfig: undefined,
  legendKey: undefined,
  onMapCreate: undefined,
  features: undefined,
  setZooming: () => {},
};

MapEvents.propTypes = {
  zoomstart: PropTypes.func,
  zoomend: PropTypes.func,
  onEdit: PropTypes.func,
  setZooming: PropTypes.func,
};

MapEvents.defaultProps = {
  setZooming: () => {},
};

export default Leaflet;
